import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import { chartTimePeriod } from '../../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import report from '../../../../utils/api/reports';

interface ChartDataProperties {
  date: string;
  totalPowerConsumed: number;
  peakPowerConsumed: number;
  basePowerConsumed: number;
  offPeakPowerConsumed: number;
  documentCount: number;
}

/**
 * Custom square bar component
 */
function TriangleBar(properties: any) {
  const { fill, x, y, width, height } = properties;

  const blockHeight = 5;
  const blockGap = 2;
  const blockCount = Math.floor(height / (blockHeight + blockGap));

  return (
    <>
      {Array.from({ length: blockCount }).map((_, index) => (
        <rect
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          x={x}
          y={y + height - (index + 1) * (blockHeight + blockGap)}
          width={width}
          height={blockHeight}
          fill={fill}
          onClick={() => { }}
          style={{ cursor: 'pointer' }}
        />
      ))}
    </>
  );
}

/**
 *
 * EnergyReportRevenue component
 */
function EnergyReportRevenue() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  /**
   * Function to show custom tooltip
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="energy-report-tooltip">
          <p className="label">{data?.label || data?.stationName}</p>
          <div className="tooltip-values">
            <p style={{ color: '#4ABDFB' }}>{t('basePowerConsumed')}: {data?.basePowerConsumed}kwh</p>
            <p style={{ color: '#E41010' }}>
              {t('offPeakPowerConsumed')}: {data?.offPeakPowerConsumed}kwh
            </p>
            <p style={{ color: '#2E9E3A' }}>{t('peakPowerConsumed')}: {data?.peakPowerConsumed}kwh</p>
            <p>{t('totalPowerConsumed')}: {data?.totalPowerConsumed}kwh</p>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await report.fetchReportchart({
        timePeriod: range,
        reportType: 'powerConsumedByRevenueTyre',
        accountCustomId: reportsData?.accountId,
      });
      let chartData: any[] = [];

      if (response.status === 200) {
        if (range === 'last12months') {
          chartData = fillMissingMonthsGeneric(response.data);
        } else if (range === 'lastMonth') {
          chartData = fillMissingLastNDaysGeneric(response.data, 30);
        } else if (range === 'last7days') {
          chartData = fillMissingLastNDaysGeneric(response.data, 7);
        } else if (range === 'last24hours') {
          chartData = fillMissingLast24HrsGeneric(response.data);
        }
        // 2️⃣ Normalize all data (divide by 1000)
        chartData = chartData.map((item) => ({
          ...item,
          peakPowerConsumed:Number ((item.peakPowerConsumed / 1000).toFixed(2)) || 0,
          basePowerConsumed: Number ((item.basePowerConsumed / 1000).toFixed(2)) || 0,
          offPeakPowerConsumed: Number ((item.offPeakPowerConsumed / 1000).toFixed(2)) || 0,
          totalPowerConsumed: Number ((item.totalPowerConsumed / 1000).toFixed(2)) || 0,
        }));
      }

      setBarGraphData(chartData);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };
  /**
   * If clicking the already active pin, deactivate it
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="energy-report-chart">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin energy consumption trend based on revenue tiers"
              className={`pin-button ${reportsData.activePinName.includes('powerConsumedByRevenueTyre') ? 'active' : ''}`}
              onClick={() => handleClick('powerConsumedByRevenueTyre')}>
              {reportsData.activePinName.includes('powerConsumedByRevenueTyre') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>

            <p>{t('energyConsumptionRevenueTiers')}</p>
          </div>
          <CustomToggleButton
            labels={chartTimePeriod.map((duration: { label: any }) => t(duration.label))}
            onToggle={(index) =>
              fetchPowerConsumedReport(
                chartTimePeriod[index]?.value || 'last12months',
              )
            }
            defaultActiveIndex={0}
            type="chart"
          />
        </div>
        <div className="energy-report-container">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barGraphData}>
              <XAxis
                dataKey="label" // Assuming 'label' has 'Jan', 'Feb', etc.
                axisLine
                tickLine={false}
                tick={{ fontSize: 12 }}
              />

              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Bar
                dataKey="basePowerConsumed"
                name={t('basePower')}
                fill="#4ABDFB"
                shape={<TriangleBar />}
                barSize={5}
              />
              <Bar
                dataKey="peakPowerConsumed"
                name={t('peakPower')}
                fill="#2E9E3A"
                shape={<TriangleBar />}
                barSize={5}
              />
              <Bar
                dataKey="offPeakPowerConsumed"
                name={t('offPeakPower')}
                fill="#E41010"
                shape={<TriangleBar />}
                barSize={5}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="energy-report-chart-label">
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#4ABDFB' }} />
          <p>{t('base')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#2E9E3A' }} />
          <p>{t('peak')}</p>
        </div>
        <div className="each-label">
          <div className="each-label-color" style={{ backgroundColor: '#E41010' }} />
          <p>{t('offPeak')}</p>
        </div>
      </div>
    </div>
  );
}

export default EnergyReportRevenue;
